import * as React from 'react'
import type { EventDetailDto } from '@lib/dto/event.dto'
import { dollarsFromCents } from '@lib/helpers/format'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useUpdateEvent } from '@/hooks/useEvents'
import { centsToInput, dollarsToCents } from '@/lib/price'

const HINT = 'What the couple booked in HoneyBook, for return on ad spend.'

/**
 * What the couple of a HoneyBook booking's event booked (docs/honeybook.plan.md,
 * phase 6). HoneyBook's API doesn't say, so staff type it; it counts toward
 * return on ad spend wherever Sales records nothing, and stays typable once
 * HoneyBook no longer manages the event. Staff whose role only views events
 * read it.
 *
 * Mounted with `key={bookedValueCents}`, so a saved change starts the draft
 * afresh rather than being copied in by an effect.
 */
export function HoneyBookBookedValue({ event, canEdit }: { event: EventDetailDto; canEdit: boolean }) {
  const saved = event.honeybook?.bookedValueCents ?? null
  const update = useUpdateEvent(event.id)
  const [draft, setDraft] = React.useState(centsToInput(saved))
  const [failure, setFailure] = React.useState<string | null>(null)
  const cents = dollarsToCents(draft)
  const invalid = cents === undefined
  const changed = invalid || cents !== saved

  function save(): void {
    if (cents === undefined) return
    setFailure(null)
    update.mutate({ bookedValueCents: cents }, { onError: (e) => setFailure(requestErrorMessage(e)) })
  }

  if (!canEdit) {
    return (
      <div className="space-y-1">
        <p className="m-0 text-sm font-medium text-ink">Booked value</p>
        <p className="m-0 text-sm tabular-nums">{saved === null ? <span className="text-ink-muted">Not typed yet</span> : dollarsFromCents(saved)}</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Field label="Booked value ($)" htmlFor="honeybook-booked-value" hint={HINT} error={invalid ? 'Type the value in dollars, like 12,500.' : failure}>
        <Input
          id="honeybook-booked-value"
          inputMode="decimal"
          className="w-40 tabular-nums"
          value={draft}
          placeholder="Not typed yet"
          onChange={(e) => setDraft(e.target.value)}
        />
      </Field>
      {/* The label carries the state: "Save" while there is something to save, "Saved" when not. */}
      <Button size="sm" disabled={!changed || invalid || update.isPending} onClick={save}>
        {update.isPending ? 'Saving…' : changed ? 'Save' : 'Saved'}
      </Button>
    </div>
  )
}
